import { FC, useCallback, useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { tenantService } from '../../services/tenantService'
import { formatDate } from '../../utils/date/formatters'
import WeatherWarningModal from './WeatherWarningModal'
import { WEATHER_BUTTON_STYLE, WEATHER_BUTTON_ICON } from './weatherStatusVisuals'
import styles from './WeatherWarningButton.module.css'
import type { WeatherStatusDTO } from '../../types/dtos'

interface WeatherWarningButtonProps {
  tenantId: string
}

const WeatherWarningButton: FC<WeatherWarningButtonProps> = ({ tenantId }) => {
  const { t } = useTranslation('common')
  const [weatherStatus, setWeatherStatus] = useState<WeatherStatusDTO | null>(null)
  const [showModal, setShowModal] = useState(false)

  const fetchStatus = useCallback(async () => {
    try {
      const dto = await tenantService.getWeatherStatus(tenantId)
      setWeatherStatus(dto)
    } catch {
      // Silently ignore — button color stays at last known state
    }
  }, [tenantId])

  // Fetch once on mount to colour the icon; the modal's onSuccess DTO keeps it current after edits.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchStatus()
  }, [fetchStatus])

  const status = weatherStatus?.status ?? 'NONE'
  const buttonStyle = WEATHER_BUTTON_STYLE[status]
  const StatusIcon = WEATHER_BUTTON_ICON[status]
  const ariaLabel = t(`weather.button.aria_${status.toLowerCase()}`)

  const buildTitle = (): string => {
    if (status === 'NONE') return t('weather.button.none_tooltip')

    const parts = [t(`weather.button.${status.toLowerCase()}_tooltip`)]
    const reason = weatherStatus?.reason?.trim()
    if (reason) parts.push(`— ${reason}`)
    if (status === 'DELAYED') {
      const eta = weatherStatus?.estimatedResumeAt
      parts.push(
        `· ${eta ? t('weather.banner.eta', { time: formatDate(eta, 'time') }) : t('weather.banner.no_eta')}`,
      )
    }
    return parts.join(' ')
  }

  const handleSuccess = (dto: WeatherStatusDTO): void => {
    setWeatherStatus(dto)
  }

  return (
    <>
      <Button
        variant="link"
        className={`text-decoration-none p-1 ${styles.weatherButton}${status === 'NONE' ? ` ${styles.weatherButtonNone}` : ''}`}
        style={buttonStyle}
        aria-label={ariaLabel}
        title={buildTitle()}
        onClick={() => setShowModal(true)}
      >
        <StatusIcon size={22} />
      </Button>

      <WeatherWarningModal
        show={showModal}
        onHide={() => setShowModal(false)}
        tenantId={tenantId}
        currentStatus={weatherStatus}
        onSuccess={handleSuccess}
      />
    </>
  )
}

export default WeatherWarningButton
